import React, { useState, useEffect, useRef } from 'react'
import { Fab, Paper, Slide, IconButton, Box } from '@mui/material'
import { Chat as ChatIcon, Close as CloseIcon, DragIndicator as DragIcon } from '@mui/icons-material'
import { useWebSocket, useChatSession } from './hooks'
import { MessageList, MessageInput, ConnectionStatus } from './components'
import './FloatingChatWidget.css'

interface Message {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
}

export default function FloatingChatWidget() {
  const [isOpen, setIsOpen] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [position, setPosition] = useState({ x: window.innerWidth - 420, y: 20 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const chatRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<HTMLDivElement>(null)

  const { session, sessionId, loading: sessionLoading } = useChatSession()
  const { connect, disconnect, sendMessage, isConnected, connectionError } = useWebSocket(sessionId)
  const connectedSessionIdRef = useRef<string | null>(null)
  const hasConnectedRef = useRef<boolean>(false)

  // Connect when session is available - only once per session ID
  useEffect(() => {
    if (sessionId && !sessionLoading) {
      // Only connect if this is a new session ID and we haven't connected yet
      if (connectedSessionIdRef.current !== sessionId && !hasConnectedRef.current) {
        console.log('[FloatingChatWidget] New session ID detected, connecting...', sessionId)
        connectedSessionIdRef.current = sessionId
        hasConnectedRef.current = true
        // Use setTimeout to debounce rapid session changes
        const timeoutId = setTimeout(() => {
          connect(sessionId)
        }, 100)
        return () => clearTimeout(timeoutId)
      } else if (connectedSessionIdRef.current !== sessionId) {
        // Session ID changed - update ref but don't reconnect (singleton handles it)
        console.log('[FloatingChatWidget] Session ID changed, but singleton will handle reconnection')
        connectedSessionIdRef.current = sessionId
      }
    } else if (!sessionId) {
      // Reset when session is cleared
      connectedSessionIdRef.current = null
      hasConnectedRef.current = false
    }
  }, [sessionId, sessionLoading]) // Removed connect from dependencies

  // Clear loading state on connection error
  useEffect(() => {
    if (connectionError) {
      setIsLoading(false)
    }
  }, [connectionError])

  // Handle incoming messages from WebSocket
  useEffect(() => {
    const handleChatMessage = (event: Event) => {
      const customEvent = event as CustomEvent<{ type: string; sender?: string; message?: string; timestamp?: string }>
      const data = customEvent.detail
      if (data.type === 'message' && data.sender === 'bot' && data.message) {
        setIsLoading(false)
        const botMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: data.message,
          timestamp: new Date(data.timestamp || Date.now()),
        }
        setMessages((prev) => [...prev, botMessage])
      }
    }

    const handleSystemMessage = (event: Event) => {
      const customEvent = event as CustomEvent<{ type: string; message?: string; content?: string; timestamp?: string }>
      const data = customEvent.detail
      if (data.type === 'system' || data.type === 'connection') {
        const systemMessage: Message = {
          id: Date.now().toString(),
          role: 'system',
          content: data.message || data.content || 'Connected',
          timestamp: new Date(data.timestamp || Date.now()),
        }
        setMessages((prev) => [...prev, systemMessage])
      }
    }

    window.addEventListener('chat-message', handleChatMessage)
    window.addEventListener('system-message', handleSystemMessage)

    return () => {
      window.removeEventListener('chat-message', handleChatMessage)
      window.removeEventListener('system-message', handleSystemMessage)
    }
  }, [])

  const handleSendMessage = (content: string) => {
    if (!content.trim() || !isConnected || !sessionId) {
      console.warn('[ChatBot] Cannot send message:', { isConnected, sessionId })
      return
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMessage])
    setIsLoading(true)
    sendMessage(content.trim())
  }

  const handleToggle = () => {
    setIsOpen(!isOpen)
    if (!isOpen) {
      setIsMinimized(false)
    }
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target === dragRef.current || dragRef.current?.contains(e.target as Node)) {
      setIsDragging(true)
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      })
    }
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const newX = e.clientX - dragStart.x
      const newY = e.clientY - dragStart.y
      
      // Keep within viewport bounds
      const widgetWidth = 400
      const widgetHeight = isMinimized ? 60 : 600
      const maxX = window.innerWidth - widgetWidth
      const minY = 0
      const minYFromBottom = window.innerHeight - 70
      const maxY = minYFromBottom - widgetHeight
      
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(minY, Math.min(newY, maxY)),
      })
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, dragStart])

  // Update position on window resize
  useEffect(() => {
    const handleResize = () => {
      const widgetWidth = 400
      const widgetHeight = isMinimized ? 60 : 600
      const maxX = window.innerWidth - widgetWidth
      const minYFromBottom = window.innerHeight - 70
      const maxY = minYFromBottom - widgetHeight
      
      setPosition(prev => ({
        x: Math.max(0, Math.min(prev.x, maxX)),
        y: Math.max(0, Math.min(prev.y, maxY)),
      }))
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isMinimized])

  return (
    <>
      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="chat"
        onClick={handleToggle}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1300,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          '&:hover': {
            transform: 'scale(1.1)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          opacity: isOpen ? 0.7 : 1,
        }}
      >
        <ChatIcon />
      </Fab>

      {/* Chat Widget */}
      <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
        <Paper
          ref={chatRef}
          elevation={8}
          sx={{
            position: 'fixed',
            left: position.x,
            top: position.y,
            width: 400,
            height: isMinimized ? 'auto' : 600,
            maxHeight: isMinimized ? 'none' : window.innerHeight - 80,
            zIndex: 1301,
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#ffffff',
          }}
          onMouseDown={handleMouseDown}
        >
          {/* Header with drag handle */}
          <Box
            ref={dragRef}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1,
              backgroundColor: '#f5f5f5',
              borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
              cursor: 'move',
              minHeight: '48px',
              '&:hover': {
                backgroundColor: '#e8e8e8',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
              <DragIcon sx={{ color: '#666666', fontSize: 18, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <ConnectionStatus isConnected={isConnected} error={connectionError} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <IconButton
                size="small"
                onClick={() => setIsMinimized(!isMinimized)}
                sx={{
                  color: '#666666',
                  padding: '4px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    color: '#000000',
                  },
                }}
              >
                {isMinimized ? '□' : '—'}
              </IconButton>
              <IconButton
                size="small"
                onClick={handleToggle}
                sx={{
                  color: '#666666',
                  padding: '4px',
                  '&:hover': {
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    color: '#ef4444',
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Chat Content */}
          {!isMinimized && (
            <Box 
              sx={{ 
                flex: 1, 
                overflow: 'hidden', 
                display: 'flex', 
                flexDirection: 'column', 
                minHeight: 0,
                backgroundColor: '#efeae2',
              }}
            >
              {sessionLoading && !sessionId ? (
                <Box sx={{ p: 2, textAlign: 'center', color: '#666666' }}>
                  Creating chat session...
                </Box>
              ) : (
                <>
                  <Box sx={{ 
                    flex: 1, 
                    overflow: 'hidden', 
                    display: 'flex', 
                    flexDirection: 'column',
                    minHeight: 0,
                    maxHeight: '100%',
                    backgroundColor: '#efeae2',
                  }}>
                    <MessageList messages={messages} isLoading={isLoading} />
                  </Box>
                  <MessageInput 
                    onSendMessage={handleSendMessage} 
                    disabled={!isConnected || !sessionId}
                  />
                </>
              )}
            </Box>
          )}
        </Paper>
      </Slide>
    </>
  )
}

